import {type GridSizeProp} from '@logistics-frontend/blocks'
import {get, head, pipe, toDefaultEmptyString} from '@logistics-frontend/utils'
import {useUpdateEffect} from '@logistics-frontend/utils/hooks'
import {useFormContext} from '@yandex-delivery/form'
import {useCallback, useMemo, useState} from 'react'

import type {Order, TariffVertical} from 'src/shared/types'

import {BUSINESS_VERTICALS} from '../consts'
import {useFetchVerticals} from '../use-fetch-verticals'

export function useDeliveryDirectionVerticals() {
  const {isVerticalsPending} = useFetchVerticals()
  const {setFieldValue, watch} = useFormContext<Order>()
  const [isBannerVisible, setIsBannerVisible] = useState(false)

  const isBusiness = watch('isBusiness')
  const selectedVerticalName = watch('offerFlow.vertical.name')
  const selectedDeliveryDirection = watch('offerFlow.deliveryDirection')
  const verticalGroups = watch('__settings.__deliveryDirectionsVerticals')

  const verticals: TariffVertical[] = useMemo(() => {
    if (selectedDeliveryDirection === undefined || verticalGroups === undefined) {
      return []
    }

    const verticalsInGroup = verticalGroups[selectedDeliveryDirection]

    if (verticalsInGroup === undefined) {
      return []
    }

    // TODO - учесть, что BUSINESS_VERTICALS чисто тема /order. Важно инкапсулировать эту логику в widget для /order
    if (isBusiness) {
      return verticalsInGroup
    }

    // TODO - учесть, что BUSINESS_VERTICALS чисто тема /order. Важно инкапсулировать эту логику в widget для /order
    return verticalsInGroup.filter(vertical => !BUSINESS_VERTICALS.includes(vertical.id))
  }, [isBusiness, selectedDeliveryDirection, verticalGroups])

  let cellSize: GridSizeProp

  switch (verticals.length) {
    case 4:
      cellSize = 3
      break
    case 6:
    case 3:
      cellSize = 4
      break
    case 2:
      cellSize = 6
      break
    case 1:
      cellSize = 6
      break
    default:
      cellSize = 'auto'
      break
  }

  if (isVerticalsPending) {
    cellSize = 3
  }

  const toggleBannerVisibilityHandler = useCallback(() => {
    setIsBannerVisible(prev => !prev)
  }, [])

  /**
   * @BusinessLogic:
   * При смене направления доставки могут меняться вертикали. Например, количество вертикалей по России меньше
   * чем количество вертикалей по городу. Поэтому, если выбранная вертикаль не подходит для выбранного направления
   * доставки, то нужно выбрать первую вертикаль из списка.
   */
  useUpdateEffect(() => {
    if (selectedVerticalName === undefined) {
      return
    }

    const vertical = verticals.find(vertical => vertical.id === selectedVerticalName)


    if (BUSINESS_VERTICALS.includes(vertical?.id)) {
      toggleBannerVisibilityHandler()
    }

    if ((vertical || vertical === undefined) && !BUSINESS_VERTICALS.includes(vertical?.id)) {
      return
    }

    const firstVerticalName = pipe(verticals, head, get('id'), toDefaultEmptyString)
    setFieldValue('offerFlow.vertical.name', firstVerticalName)
  }, [selectedDeliveryDirection, selectedVerticalName, setFieldValue, verticalGroups, verticals])

  return {
    isVerticalsPending,
    cellSize,
    verticals: isVerticalsPending ? undefined : verticals,
    toggleBannerVisibilityHandler,
    isBannerVisible,
  } as const
}
